import { createApi, type BaseFetchQueryFnArgs } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { transformResponseWithCurrentUserPushMessages } from 'BackRoyalApi/transformResponseWithCurrentUserPushMessages';
import { type BaseQueryApi, type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { angularInjectorProvider } from 'Injector';
import { type FrontRoyalRootScope } from 'FrontRoyalAngular';
import { getProgressSuspendableResource } from 'ContentProgress';
import { UPLOAD_PROJECT_SUBMISSION_ROUTE } from './constants';
import {
    type ProjectSubmissionUpload,
    type ProjectSubmissionUploadAttrs,
    type ProjectSubmissionUploadAttrsForRequest,
    type ProjectSubmissionAttachmentForUpload,
    type UploadProjectSubmissionResponse,
} from './ProjectSubmission.types';
import { shouldUseBase64Upload, convertFileToBase64 } from './capacitorFileUtils';

// Type for the baseQuery parameter passed to queryFn (already has api and extraOptions applied)
// MaybePromise allows both sync and async returns, matching RTK Query's internal type
type QueryFnBaseQueryResult = { data?: unknown; error?: FetchBaseQueryError; meta?: Record<string, unknown> };
type QueryFnBaseQuery = (args: BaseFetchQueryFnArgs) => QueryFnBaseQueryResult | PromiseLike<QueryFnBaseQueryResult>;

// Check if any attachments contain files (requiring FormData or base64 encoding)
const attachmentsHaveFiles = (upload: ProjectSubmissionUploadAttrs): boolean =>
    upload.attachments.some(attachment => attachment.file);

/**
 * On Capacitor native platforms, CapacitorHttp corrupts FormData file uploads.
 * This function converts files to base64 and sends them as JSON instead.
 *
 * @param upload - The upload attributes with File objects
 * @returns Upload attributes with base64-encoded files for JSON transmission
 */
const convertUploadToBase64 = async (
    upload: ProjectSubmissionUploadAttrs,
): Promise<ProjectSubmissionUploadAttrsForRequest> => {
    // Convert attachments: File -> base64, keep URLs as-is
    const convertedAttachments: ProjectSubmissionAttachmentForUpload[] = await Promise.all(
        upload.attachments.map(async attachment => {
            if (attachment.file) {
                const fileBase64 = await convertFileToBase64(attachment.file);
                return {
                    file_base64: fileBase64,
                    submission_format_type: attachment.submission_format_type,
                };
            }
            // URL attachments don't need conversion
            return attachment;
        }),
    );

    // Build request object with base64-encoded files
    const requestData: ProjectSubmissionUploadAttrsForRequest = {
        metadata: upload.metadata,
        project_type: upload.project_type,
        requirement_identifier: upload.requirement_identifier,
        submitted_for_learner_project_id: upload.submitted_for_learner_project_id,
        user_id: upload.user_id,
        attachments: convertedAttachments,
        selected_project_variant: upload.selected_project_variant,
    };

    // Convert group agreement file if present
    if (upload.group_project_agreement_file) {
        requestData.group_project_agreement_file_base64 = await convertFileToBase64(
            upload.group_project_agreement_file,
        );
    }

    return requestData;
};

// Build FormData for uploads with attachments containing files (web platform)
// Rails expects indexed arrays like: attachments[0][file], attachments[0][submission_format_type], etc.
const buildFormDataWithAttachments = (upload: ProjectSubmissionUploadAttrs): FormData => {
    const formData = new FormData();

    // Add base record fields
    formData.append('record[user_id]', upload.user_id);
    formData.append('record[project_type]', upload.project_type);
    formData.append('record[requirement_identifier]', upload.requirement_identifier);
    formData.append('record[submitted_for_learner_project_id]', upload.submitted_for_learner_project_id);
    formData.append('record[metadata][cohort_id]', upload.metadata.cohort_id);
    if (upload.metadata.from_project_eval_modal) {
        formData.append('record[metadata][from_project_eval_modal]', 'true');
    }

    // Add group agreement file if present
    if (upload.group_project_agreement_file) {
        formData.append('record[group_project_agreement_file]', upload.group_project_agreement_file);
    }

    // Add selected project variant if present
    if (upload.selected_project_variant) {
        formData.append('record[selected_project_variant]', upload.selected_project_variant);
    }

    // Add attachments with indexed keys for Rails
    // Only include attachments that have either a file or URL (defensive check)
    const attachmentsWithFiles = upload.attachments.filter(attachment => attachment.file || attachment.url);

    for (let index = 0; index < attachmentsWithFiles.length; index++) {
        const attachment = attachmentsWithFiles[index];

        formData.append(`record[attachments][${index}][submission_format_type]`, attachment.submission_format_type);

        if (attachment.file) {
            formData.append(`record[attachments][${index}][file]`, attachment.file);
        } else if (attachment.url) {
            formData.append(`record[attachments][${index}][url]`, attachment.url);
        }
    }

    return formData;
};

// Exported for testing
export { buildFormDataWithAttachments, convertUploadToBase64, attachmentsHaveFiles };

export const uploadProjectSubmissionApi = createApi('UploadProjectSubmission', {
    ...getBackRoyalApiBaseConfig('UploadProjectSubmission'),
    endpoints: builder => ({
        create: builder.mutation<UploadProjectSubmissionResponse, ProjectSubmissionUploadAttrs>({
            async queryFn(
                projectSubmissionUpload: ProjectSubmissionUploadAttrs,
                _api: BaseQueryApi,
                _options: Record<string, unknown>,
                fetchWithBaseQuery: QueryFnBaseQuery,
            ) {
                const hasFiles =
                    attachmentsHaveFiles(projectSubmissionUpload) ||
                    projectSubmissionUpload.group_project_agreement_file;

                let body: ProjectSubmissionUpload | FormData;

                if (hasFiles && shouldUseBase64Upload()) {
                    // On Capacitor native platforms: Convert files to base64 and send as JSON
                    // This bypasses CapacitorHttp's FormData corruption issue
                    const base64Upload = await convertUploadToBase64(projectSubmissionUpload);
                    body = { record: base64Upload };
                } else if (hasFiles) {
                    // On web: Use FormData for file uploads
                    body = buildFormDataWithAttachments(projectSubmissionUpload);
                } else {
                    // No files: Send as JSON
                    body = { record: projectSubmissionUpload };
                }

                const response = (await fetchWithBaseQuery({
                    url: UPLOAD_PROJECT_SUBMISSION_ROUTE,
                    method: 'POST',
                    body,
                })) as {
                    data?: UploadProjectSubmissionResponse;
                    error?: FetchBaseQueryError;
                };

                if (response.error) {
                    return { error: response.error };
                }

                // Api::ProjectSubmissionsController#upload will create a ProjectProgress for the ProjectSubmission.
                // We want to do a few things here (and block the completion of this mutation until they're done):
                //  1. Force UserProgressLoader to refetch progress using the max updated_at currently in store, which at this point will not include in the ProjectProgress we just created.
                //     Ordinarily, this would be handled by UserProgressLoaderInterceptor, but this is the first API call we've written that creates progress outside of Angular.js, meaning UserProgressLoaderInterceptor won't run on this response.
                //  2. Force a reload of UserProgressLoader.suspendableResource because the value will be cached and won't be reloaded until UserProgressLoader.suspendableResource pings on its own interval.
                //
                // This is hopefully a temporary solution, ideally we would be querying for progress via RTKQ and simply invalidating tags here.
                const { pushMessages } = response.data?.meta as UploadProjectSubmissionResponse['meta'];
                const currentUser = angularInjectorProvider.get<FrontRoyalRootScope>('$rootScope').currentUser;
                const userProgressLoader = currentUser?.progress;
                if (userProgressLoader) {
                    const progressMaxUpdatedAt = await userProgressLoader.getProgressMaxUpdatedAt();
                    await userProgressLoader.onUnfetchedUpdatesDetected(progressMaxUpdatedAt);
                    await userProgressLoader.suspendableResource.load().promise;
                    // Also force-reload the React-side module-level resource used by useContentProgress.
                    // Without this, components like ProjectSubmissionForm won't see the new ProjectProgress
                    // until the 60-second ping interval fires.
                    if (currentUser.id) {
                        await getProgressSuspendableResource(currentUser.id).load().promise;
                    }
                }

                // Push any updated properties onto the CurrentUserIguanaObject.
                // This is important because this mutation can specifically change the past_due_projects on a user's program inclusion.
                transformResponseWithCurrentUserPushMessages(pushMessages);

                return { data: response.data as UploadProjectSubmissionResponse };
            },
        }),
    }),
});
